'use client';

import { Icon } from '@iconify/react/dist/iconify.js';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { m } from 'framer-motion';
import { useState } from 'react';
import { MotionContainer, varBounce } from 'src/components/animate';
import { Image } from 'src/components/image';
import { themeConfig } from 'src/theme';

interface Facility {
  icon: string;
  title: string;
}

interface BuseTypes {
  name: string;
  facilities: Facility[];
  seatLayout: string;
}

const BUS_TYPES: BuseTypes[] = [
  {
    name: 'Executive 30 Seats',
    facilities: [
      {
        icon: 'iconoir:tv',
        title: 'LCD TV',
      },
      {
        icon: 'lucide:air-vent',
        title: 'AC',
      },
      {
        icon: 'cil:toilet',
        title: 'Toilet',
      },
      {
        icon: 'lucide:music',
        title: 'Entertaimnet',
      },
      {
        icon: 'game-icons:pillow',
        title: 'Bantal Leher',
      },
      {
        icon: 'game-icons:blanket',
        title: 'Selimut',
      },
      {
        icon: 'fluent:food-48-regular',
        title: 'Voucher Makan',
      },
      {
        icon: 'icon-park-solid:snacks',
        title: 'Snack',
      },
      {
        icon: 'ant-design:usb-twotone',
        title: 'USB Port',
      },
      {
        icon: 'hugeicons:seat-selector',
        title: '30 Seats',
      },
      {
        icon: 'icon-park-outline:baby-car-seat',
        title: 'Leg Rest',
      },
      {
        icon: 'mdi:car-child-seat',
        title: 'Recycling Seat',
      },
    ],
    seatLayout: '/assets/images/image-denah-kursi-bus-pariwisata-30-seats-puspajaya.png',
  },
  {
    name: 'Big Bus 48 Seats',
    facilities: [
      {
        icon: 'iconoir:tv',
        title: 'LCD TV',
      },
      {
        icon: 'lucide:air-vent',
        title: 'AC',
      },
      {
        icon: 'lucide:music',
        title: 'Entertaimnet',
      },
      {
        icon: '/assets/icons/components/ic-dispenser.svg',
        title: 'Dispenser',
      },
      {
        icon: 'ant-design:usb-twotone',
        title: 'USB Port',
      },
      {
        icon: 'hugeicons:seat-selector',
        title: '48 Seats',
      },
      {
        icon: 'mdi:car-child-seat',
        title: 'Recycling Seat',
      },
    ],
    seatLayout: '/assets/images/image-denah-kursi-bus-pariwisata-48-seats-puspajaya.png',
  },
  {
    name: 'Big Bus 59 Seats',
    facilities: [
      {
        icon: 'iconoir:tv',
        title: 'LCD TV',
      },
      {
        icon: 'lucide:air-vent',
        title: 'AC',
      },
      {
        icon: 'lucide:music',
        title: 'Entertaimnet',
      },
      {
        icon: '/assets/icons/components/ic-dispenser.svg',
        title: 'Dispenser',
      },
      {
        icon: 'ant-design:usb-twotone',
        title: 'USB Port',
      },
      {
        icon: 'hugeicons:seat-selector',
        title: '59 Seats',
      },
      {
        icon: 'mdi:car-child-seat',
        title: 'Recycling Seat',
      },
    ],
    seatLayout: '/assets/images/image-denah-kursi-bus-pariwisata-59-seats-puspajaya.png',
  },
  {
    name: 'Medium Bus 33 Seats',
    facilities: [
      {
        icon: 'iconoir:tv',
        title: 'LCD TV',
      },
      {
        icon: 'lucide:air-vent',
        title: 'AC',
      },
      {
        icon: 'lucide:music',
        title: 'Entertaimnet',
      },
      {
        icon: 'ant-design:usb-twotone',
        title: 'USB Port',
      },
      {
        icon: 'hugeicons:seat-selector',
        title: '33 Seats',
      },
      {
        icon: 'mdi:car-child-seat',
        title: 'Recycling Seat',
      },
    ],
    seatLayout: '/assets/images/image-denah-kursi-bus-pariwisata-33-seats-puspajaya.png',
  },
];

export default function Page() {
  const [selectedBus, setSelectedBus] = useState<BuseTypes>(BUS_TYPES[0]);

  const selectBus = (bus: BuseTypes) => {
    setSelectedBus(bus);
  };

  return (
    <Box>
      <Box
        sx={{
          width: '100%',
          borderRadius: {
            xs: '0px 0px 20px 20px',
            md: '0px 0px 50px 50px',
          },
          borderBottom: '5px solid var(--secondary-main, #FEFE01)',
          background:
            'var(--gradient-primary-bold, linear-gradient(135deg, var(--primary-main, #11BB18) 0%, var(--primary-dark, #007867) 100%))',
          boxShadow:
            'var(--color-x, 0px) var(--color-y, 8px) var(--color-blur, 16px) var(--color-spread, 0px) var(--primary-24, rgba(0, 167, 111, 0.24))',
        }}
      >
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: { xs: 0, md: '10px' },
              right: '0px',
              width: '600px',
              height: '600px',
              backgroundImage: 'url("/logo/logo-single.png")',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              opacity: 0.05,
              zIndex: 2,
            },
          }}
        >
          <Container sx={{ py: '40px' }}>
            <Typography variant="h2" color="secondary">
              Bus Pariwisata
            </Typography>
          </Container>
        </Box>
        <Container
          sx={{
            position: 'relative',
            height: {
              xs: 'calc(20vh)',
              sm: 'calc(40vh)',
            },
            paddingBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <MotionContainer
            sx={{
              bottom: '-80px',
              position: 'absolute',
              width: '90%',
              zIndex: 88,
            }}
          >
            <Box
              component={m.img}
              variants={varBounce('inLeft')}
              width="100%"
              src="/assets/images/image-bus-pariwisata.webp"
              alt="Bus Pariwisata Puspa Jaya"
            />
          </MotionContainer>
        </Container>
      </Box>
      <div className="py-[150px]">
        <Container>
          <div className="flex flex-col gap-[38px] md:gap-[50px] mb-[78px]">
            <Typography variant="h3" textAlign="center">
              Fasilitas & Denah Kursi Bus Puspa Jaya Pariwisata
            </Typography>
            <div className="flex flex-wrap justify-center items-center gap-[16px]">
              {BUS_TYPES.map((e) => (
                <Button
                  variant={e.name === selectedBus.name ? 'contained' : 'outlined'}
                  color="primary"
                  startIcon={<Icon icon="uil:bus" />}
                  onClick={() => selectBus(e)}
                  sx={{
                    background:
                      e.name === selectedBus.name ? themeConfig.gradient.primary.bold : null,
                  }}
                >
                  {e.name}
                </Button>
              ))}
            </div>
            <div className="flex flex-col md:flex-row items-stretch gap-[16px] md:gap-[80px]">
              <div>
                <Grid
                  container
                  spacing="20px"
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  {selectedBus.facilities.map((e) => (
                    <Grid size={{ xs: 4, md: 3 }}>
                      <Paper
                        elevation={4}
                        sx={{
                          xs: '10px',
                          md: '20px',
                          aspectRatio: '1/1',
                        }}
                      >
                        <div className="flex flex-col justify-center items-center w-full h-full gap-[4px]">
                          {e.icon.endsWith('.svg') && (
                            <Image src={e.icon} alt="icon" sx={{ width: '30%' }} />
                          )}
                          {!e.icon.endsWith('.svg') && <Icon icon={e.icon} width="30%" />}
                          <Typography variant="h6" textAlign="center">
                            {e.title}
                          </Typography>
                        </div>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </div>
              <div>
                <Paper
                  elevation={4}
                  sx={{
                    height: '100%',
                    width: {
                      xs: '100%',
                      md: '300px',
                    },
                    borderRadius: {
                      xs: '10px',
                      md: '20px',
                    },
                  }}
                >
                  <div className="p-[18px] flex flex-col items-center gap-[16px]">
                    <Typography variant="h5">Denah Kursi</Typography>
                    <Image
                      sx={{
                        width: '160px',
                        borderRadius: {
                          xs: '10px',
                          md: '20px',
                        },
                      }}
                      src={selectedBus.seatLayout}
                      alt="Fasilitas kursi pada bus AKAP Puspajaya"
                    />
                  </div>
                </Paper>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </Box>
  );
}
